import React, { useEffect, useRef, useState } from 'react';
import { alpha, Box, Stack } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import { HtmlTooltip } from 'components/html-tooltip';
import { useDebouncedCallback } from 'use-debounce';
import { ITileInfoIconProps } from './types';
import { Instance } from '@popperjs/core';

export const TileInfoIcon = function ({ style, defaultStyle, tile, children }: ITileInfoIconProps) {
    const [mouseOverInfoIcon, setMouseOverInfoIcon] = useState(false);
    const iconRef = useRef<any | null>(null);
    const popperRef = useRef<Instance>(null);
    const positionRef = useRef<{ x: number; y: number }>({
        x: 0,
        y: 0,
    });

    const debouncedHide = useDebouncedCallback((value) => {
        setMouseOverInfoIcon(value);
    }, 100);

    if (undefined === defaultStyle)
        defaultStyle = {
            position: 'absolute',
            top: '4px',
            right: '10px',
            display: 'flex',
        };

    const setMaxHeight = () => {
        if (popperRef.current) {
            const popperEl = popperRef.current.state?.elements?.popper;
            if (popperEl) {
                const direction = popperRef.current?.state?.styles?.popper?.bottom == '0' ? 'top' : 'bottom';
                const posY = popperRef.current?.state?.rects?.reference?.y ?? 0;
                const windowHeight = document.documentElement.clientHeight;
                const indent = 8;
                let maxHeight = 0;
                if (direction == 'top') {
                    const topBarHeight = 40;
                    maxHeight = posY + indent - topBarHeight;
                } else {
                    maxHeight = windowHeight - posY - indent;
                }

                popperEl.style.maxHeight = maxHeight + 'px';
                popperRef.current.update();
            }
        }
    };
    useEffect(() => {
        if (mouseOverInfoIcon) {
            setTimeout(setMaxHeight, 100);

            if (iconRef?.current) {
                const tileEl = iconRef.current.closest('.tile-element');
                if (tileEl) tileEl.classList.add('state--hover');
            }
        } else {
            if (iconRef?.current) {
                const tileEl = iconRef.current.closest('.tile-element');
                if (tileEl) tileEl.classList.remove('state--hover');
            }
        }
    }, [mouseOverInfoIcon]);

    return (
        <HtmlTooltip
            placement={'right-start'}
            arrow={false}
            certified={tile.certified_ind === 'Y'}
            sx={{
                display: 'flex',
            }}
            slotProps={{
                tooltip: {
                    sx: {
                        margin: '0 !important',
                        border: '0 !important',
                        boxShadow: 'none !important',
                        background: 'none !important',
                    },
                },
                popper: {
                    sx: {},
                },
            }}
            PopperProps={{
                popperRef,
                anchorEl: {
                    getBoundingClientRect: () => {
                        return new DOMRect(positionRef.current.x, positionRef.current.y, 16, 16);
                    },
                },
            }}
            title={
                <Box
                    sx={{
                        maxHeight: '100%',
                        px: 1,
                        display: 'flex',
                    }}
                    onMouseLeave={() => {
                        debouncedHide(false);
                    }}
                    onMouseEnter={() => {
                        debouncedHide.cancel();
                        setMouseOverInfoIcon(true);
                    }}
                >
                    <Stack
                        sx={{
                            overflow: 'auto',
                            maxHeight: '100%',
                            border: '1px solid',
                            borderColor: (theme) =>
                                tile.certified_ind === 'Y' ? theme.palette.success.main : theme.palette.primary.main,
                            borderRadius: '3px',
                            boxShadow: (theme) => `0px 1px 4px 0px ${alpha(theme.palette.text.primary, 0.32)}`,
                            backgroundColor: 'background.default',
                        }}
                        onClick={(event) => {
                            //@ts-ignore
                            if (event?.target?.localName != 'a') {
                                event.preventDefault();
                            }
                            event.stopPropagation();
                        }}
                    >
                        {children}
                    </Stack>
                </Box>
            }
            open={mouseOverInfoIcon}
        >
            <Box
                ref={iconRef}
                sx={{ ...style, ...defaultStyle }}
                onMouseLeave={() => {
                    debouncedHide(false);
                }}
                onMouseEnter={(event) => {
                    debouncedHide.cancel();
                    const elementCoor = (event.target as HTMLElement).getBoundingClientRect();
                    positionRef.current = { x: elementCoor.left, y: elementCoor.top };
                    setMouseOverInfoIcon(true);
                }}
                className={`tile-element__info ${tile.certified_ind === 'Y' ? 'tile-element__info--certified' : ''}`}
            >
                {tile.certified_ind === 'Y' ? (
                    <Box
                        sx={{
                            display: 'flex',
                            color: tile.certification_level_color || 'success.main',
                            fontSize: '16px',
                        }}
                    >
                        <IconMi icon={'certified'} />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            color: (theme) => alpha(theme.palette.text.primary, 0.64),
                            fontSize: '16px',
                        }}
                    >
                        <IconMi icon={'info-circle'} />
                    </Box>
                )}
            </Box>
        </HtmlTooltip>
    );
};
